import { type FC, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import ChatAvatar from '@/components/ui/chat-avatar';
import { useComposition } from '@/hooks/use-composition';
import { useTimeAgo } from '@/hooks/use-time-ago';
import { cn } from '@/lib/utils';

const chatMessageBubbleVariants = cva(
  'col-span-2 row-start-1 box-content break-words',
  {
    variants: {
      variant: {
        default:
          'bg-chat-message-background border-chat-message-border border p-2 text-secondary-foreground rounded-lg text-sm font-normal whitespace-pre-wrap',
        slot: 'ml-2',
      },
      side: {
        left: 'mr-auto col-start-2',
        right: 'ml-auto col-start-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      side: 'left',
    },
  }
);
interface ChatMessageBubbleProps
  extends VariantProps<typeof chatMessageBubbleVariants> {
  children: ReactNode;
  className?: string;
}
const ChatMessageBubble: FC<ChatMessageBubbleProps> = ({
  children,
  className,
  ...props
}) => (
  <div className={cn(chatMessageBubbleVariants(props), className)}>
    {children}
  </div>
);

const chatMessageTimestampVariants = cva(
  'text-muted-foreground row-start-2 col-start-2 text-xs',
  {
    variants: {
      side: {
        left: 'text-left flex justify-start ml-2',
        right: 'text-right flex justify-end mr-2',
      },
    },
    defaultVariants: {
      side: 'left',
    },
  }
);
interface ChatMessageTimestampProps
  extends VariantProps<typeof chatMessageTimestampVariants> {
  date: Date;
}
const ChatMessageTimestamp: FC<ChatMessageTimestampProps> = ({
  date,
  side,
}) => {
  const timeAgo = useTimeAgo(date);
  return (
    <span className={cn(chatMessageTimestampVariants({ side }))}>
      {timeAgo}
    </span>
  );
};
ChatMessageTimestamp.displayName = 'ChatMessageTimestamp';

const chatMessageMetadataVariants = cva(
  'text-muted-foreground row-start-2  text-xs',
  {
    variants: {
      side: {
        left: 'text-right col-start-3 flex justify-end',
        right: 'text-left col-start-1 flex justify-start',
      },
    },
    defaultVariants: {
      side: 'left',
    },
  }
);
interface ChatMessageMetadataProps
  extends VariantProps<typeof chatMessageMetadataVariants> {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}
const ChatMessageMetadata: FC<ChatMessageMetadataProps> = ({
  children,
  className,
  side,
  onClick,
}) => (
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
  <span
    className={cn(chatMessageMetadataVariants({ side }), className)}
    onClick={onClick}>
    {children}
  </span>
);
ChatMessageMetadata.displayName = 'ChatMessageMetadata';

interface ChatMessageComposition {
  Avatar: typeof ChatAvatar;
  Timestamp: typeof ChatMessageTimestamp;
  Metadata: typeof ChatMessageMetadata;
}

const chatMessageVariants = cva(
  'grid grid-rows-[repeat(2,minmax(0,auto))] gap-1 m-1 max-w-[70%]',
  {
    variants: {
      side: {
        left: 'mr-auto grid-cols-[auto_1fr_1fr]',
        right: 'ml-auto grid-cols-[1fr_1fr_auto]',
      },
    },
    defaultVariants: {
      side: 'left',
    },
  }
);
interface ChatMessageProps
  extends VariantProps<typeof chatMessageVariants>,
    VariantProps<typeof chatMessageBubbleVariants> {
  children: ReactNode;
}
const Root: FC<ChatMessageProps> = ({ children: _children, ...props }) => {
  const [children, avatars, timestamps, metadata] = useComposition(
    _children,
    ChatAvatar.displayName,
    ChatMessageTimestamp.displayName,
    ChatMessageMetadata.displayName
  );

  return (
    <article className={cn(chatMessageVariants(props))}>
      <ChatMessageBubble {...props}>{children}</ChatMessageBubble>
      {avatars}
      {metadata}
      {timestamps}
    </article>
  );
};
Root.displayName = 'ChatMessage';

const RootWithComposition: typeof Root & ChatMessageComposition = Object.assign(
  Root,
  {
    Avatar: ChatAvatar,
    Timestamp: ChatMessageTimestamp,
    Metadata: ChatMessageMetadata,
  }
);

export { RootWithComposition as ChatMessage };
